import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, 
  Check, 
  X, 
  Clock,
  User,
  Film,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AssociationRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      const url = filter ? `${API_URL}/api/association-requests?status=${filter}` : `${API_URL}/api/association-requests`;
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        setRequests(await response.json());
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (requestId, action) => {
    try {
      const response = await fetch(`${API_URL}/api/association-requests/${requestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action })
      });

      if (response.ok) {
        // Update local state
        setRequests(requests.map(r => 
          r.request_id === requestId 
            ? { ...r, status: action === 'approve' ? 'approved' : 'rejected' }
            : r
        ));
      }
    } catch (error) {
      console.error('Error reviewing request:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="editorial-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/cms" className="text-xl editorial-serif">Cultural Revolution</Link>
            <span className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1 bg-muted">
              Association Requests
            </span>
            {pendingCount > 0 && (
              <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                {pendingCount} pending
              </span>
            )}
          </div>
          <Link to="/cms">
            <Button variant="ghost" size="sm">
              <ArrowLeft size={16} className="mr-2" />
              Back to CMS
            </Button>
          </Link>
        </div>
      </header>

      <div className="editorial-container py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl editorial-serif mb-2">Association Requests</h1>
              <p className="text-muted-foreground">Review artist requests to join projects and companies</p>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            {['pending', 'approved', 'rejected', ''].map((status) => (
              <Button
                key={status || 'all'}
                variant={filter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(status)}
              >
                {status === '' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>

          {/* Requests List */}
          <div className="space-y-4">
            {requests.map((req) => (
              <Card key={req.request_id} className="p-4" data-testid={`request-${req.request_id}`}>
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    {req.target_type === 'project' ? (
                      <Film size={18} className="text-muted-foreground" />
                    ) : (
                      <Building2 size={18} className="text-muted-foreground" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <User size={14} className="text-muted-foreground" />
                      <span className="font-medium">{req.artist_name}</span>
                      <span className="text-muted-foreground">wants to join</span>
                    </div>
                    <p className="text-lg font-medium">{req.target_name}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">{req.target_type}</p>
                    
                    {/* Status Badge */}
                    {req.status !== 'pending' && (
                      <span className={`inline-block mt-2 text-xs uppercase tracking-wider px-2 py-1 rounded ${
                        req.status === 'approved' ? 'bg-green-500/20 text-green-600' :
                        'bg-red-500/20 text-red-600'
                      }`}>
                        {req.status}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  {req.status === 'pending' && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => handleReview(req.request_id, 'approve')}
                        data-testid={`approve-${req.request_id}`}
                      >
                        <Check size={16} className="mr-1" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleReview(req.request_id, 'reject')}
                        data-testid={`reject-${req.request_id}`}
                      >
                        <X size={16} className="mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {requests.length === 0 && (
            <div className="text-center py-12">
              <Clock size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                {filter === 'pending' ? 'No pending requests' : 'No requests found'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssociationRequestsPage;
